import { Button } from "@fieldwork/ui/components/button";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import z from "zod";

import { Chip, errorMessage, Page } from "@/components/ui-bits";
import { client, orpc } from "@/utils/orpc";

/**
 * Landing page for Plane links opened without a Plane session (the Plane host redirects here):
 * issues a one-time ticket for the same path on the user's server and sends them back.
 */
export const Route = createFileRoute("/_auth/plane")({
  validateSearch: z.object({ to: z.string().optional() }),
  component: OpenPlane,
});

const PATH = /^\/(?!\/)[\w\-./]*$/;

function OpenPlane() {
  const { to: raw } = Route.useSearch();
  const to = raw && PATH.test(raw) && raw.length <= 200 ? raw : undefined;
  const servers = useQuery(orpc.servers.list.queryOptions());
  const [error, setError] = useState<string | null>(null);
  const live = (servers.data ?? []).filter((s) => s.status === "online");
  const open = async (id: string) => {
    try {
      const { url } = await client.servers.planeLink({ id, to });
      window.location.replace(url);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => {
    if (live.length === 1) void open(live[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.length]);

  return (
    <Page title="Opening Plane" subtitle={to ? `Task link: ${to}` : undefined}>
      {error && <p className="m-0 text-sm text-[#F48A80]">{error}</p>}
      {servers.isPending && <p className="m-0 text-sm text-[#A8A59D]">Loading…</p>}
      {servers.data && live.length === 0 && (
        <p className="m-0 text-sm text-[#A8A59D]">None of your servers is online right now, so Plane can't be opened.</p>
      )}
      {live.length === 1 && !error && <p className="m-0 text-sm text-[#A8A59D]">Signing you in to Plane on {live[0]!.name}…</p>}
      {live.length > 1 && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-[#CFCBC1]">Which server's Plane is this link for?</p>
          {live.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 rounded-md bg-[#1A1A19] px-5 py-3.5 shadow-[inset_0_0_0_1px_#2A2927]">
              <span className="flex items-center gap-2.5 font-mono">
                {s.name} <Chip value="online" />
              </span>
              <Button onClick={() => open(s.id)}>Open Plane ↗</Button>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
